import styled from '@emotion/styled';
import { IconButton, pxToRem } from '@modernhealth/vital-components';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie-player';
import successAnimation from 'src/public/animations/colorful_confetti.json';
import { useQueryParams } from 'src/shared/hooks/useQueryParams';

import { Redirect, useParams } from 'react-router-dom';
import { usePathwaysNavigation } from 'src/modules/pathways/hooks/navigation';
import tada from 'src/public/img/tada.svg';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { usePathwayQuery } from '../documents/pathway.generated';

import { ImageBottomLeft, ImageBottomRight } from '../components/PageIllustrations';
import {
    PathwaySessionComplete,
    PathwaySessionCompleteProps,
} from '../components/PathwaySessionComplete';

export const PathwaySessionCompletePage = () => {
    const queryParams = useQueryParams();
    const { locationDescriptor, navigate } = usePathwaysNavigation();
    const { t } = useTranslation();
    const { uuid } = useParams<{ uuid: string }>();
    const { data, loading } = usePathwayQuery({
        variables: {
            uuid,
        },
    });

    if (!data?.pathway || loading) {
        return <CenteredSpinner />;
    }

    const { pathway } = data;
    const { memberPathway } = pathway;

    if (!memberPathway) {
        return <Redirect to={locationDescriptor.pathways()} />;
    }

    const activeSessionIndexString = queryParams.get('activeSession');
    const activeSessionIndexNumber = activeSessionIndexString
        ? parseInt(activeSessionIndexString, 10)
        : NaN;

    // Add 1 because `activeSessionIndexNumber` and `currentSessionIndex` are 0-indexed
    const activeSession =
        activeSessionIndexNumber >= 0
            ? activeSessionIndexNumber + 1
            : memberPathway.currentSessionIndex + 1;

    const renderAvatar = () => {
        if (!memberPathway.pathwayProvider.provider.avatar) return null;

        return (
            <ConfettiContainer>
                <SuccessAnimation animationData={successAnimation} speed={0.6} play />
                <ProviderAvatar
                    src={memberPathway.pathwayProvider.provider.avatar}
                    alt={t('pathways:components.pathway_provider_card.your_pathway_provider')}
                />
            </ConfettiContainer>
        );
    };

    const renderTadaSvg = () => {
        return (
            <Image
                src={tada}
                alt={t('pathways:pathway_session_complete.session_complete.title', {
                    sessionNumber: activeSession,
                })}
            />
        );
    };

    enum SessionCompleteState {
        pathwayComplete = 'PATHWAY_COMPLETE',
        sessionComplete = 'SESSION_COMPLETE',
    }

    const SessionCompleteStateToProps: Record<SessionCompleteState, PathwaySessionCompleteProps> = {
        [SessionCompleteState.pathwayComplete]: {
            title: t('pathways:pathway_session_complete.pathway_complete.title'),
            description: t('pathways:pathway_session_complete.pathway_complete.description'),
            image: renderAvatar(),
            primaryButtonText: t(
                'pathways:pathway_session_complete.pathway_complete.action.share_feedback',
            ),
            primaryButtonAction: () => navigate.pathwayReview({ uuid }),
            secondaryButtonText: t(
                'pathways:pathway_session_complete.pathway_complete.action.return',
            ),
            secondaryButtonAction: () => navigate.pathway({ uuid }),
        },
        [SessionCompleteState.sessionComplete]: {
            title: t('pathways:pathway_session_complete.session_complete.title', {
                sessionNumber: activeSession,
            }),
            description: t(
                `pathways:pathway_session_complete.session_complete.${activeSession}.description`,
            ),
            image: renderTadaSvg(),
            primaryButtonText: t(
                'pathways:pathway_session_complete.session_complete.action.continue',
            ),
            primaryButtonAction: () => navigate.pathway({ uuid }),
        },
    };

    const completeState =
        pathway.sessionCount === activeSession
            ? SessionCompleteState.pathwayComplete
            : SessionCompleteState.sessionComplete;
    const {
        title,
        description,
        image,
        primaryButtonText,
        primaryButtonAction,
        secondaryButtonText,
        secondaryButtonAction,
    } = SessionCompleteStateToProps[completeState];

    return (
        <SessionCompleteWrapper onClose={() => navigate.pathway({ uuid })}>
            <PathwaySessionComplete
                title={title}
                description={description}
                image={image}
                primaryButtonText={primaryButtonText}
                primaryButtonAction={primaryButtonAction}
                secondaryButtonText={secondaryButtonText}
                secondaryButtonAction={secondaryButtonAction}
            />
        </SessionCompleteWrapper>
    );
};

export const SessionCompleteWrapper = ({ children, onClose }) => {
    const { t } = useTranslation();

    return (
        <Wrapper>
            {onClose ? (
                <CloseButton
                    title={t('pathways:pathway_session_complete.close')}
                    icon="x"
                    size="medium"
                    variant="secondary"
                    onClick={onClose}
                />
            ) : null}
            {children}
            <ImageBottomLeft />
            <ImageBottomRight />
        </Wrapper>
    );
};

const AVATAR_SIZE = 180;

const CloseButton = styled(IconButton)({
    position: 'absolute',
    top: pxToRem(24),
    right: pxToRem(24),
});

const Wrapper = styled.div({
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    justifyContent: 'center',
    alignContent: 'center',
    minHeight: '100vh',
});

const Image = styled.img({
    width: '60%',
    alignSelf: 'center',
    marginBottom: pxToRem(24),
});

const ConfettiContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: pxToRem(220),
});

const SuccessAnimation = styled(Lottie)({
    width: pxToRem(400),
    maxHeight: pxToRem(250),
    opacity: 0.7,
    position: 'absolute',
    overflow: 'hidden',
    left: 'calc(50% - 200px)',
});

const ProviderAvatar = styled.img({
    display: 'block',
    width: pxToRem(AVATAR_SIZE),
    height: pxToRem(AVATAR_SIZE),
    margin: '0 auto',
    border: 'none',
    borderRadius: '50%',
    objectFit: 'cover',
    objectPosition: 'center',
    marginBottom: pxToRem(32),
});
